import { useEffect, useState } from "react";
import api from "@/lib/api";
import { SeeItInAction } from "@/components/SeeItInAction";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import RiskRegister from "@/components/RiskRegister";
import ActionItems from "@/components/ActionItems";
import {
  Loader2, Plus, Scale, AlertTriangle, ClipboardList, ShieldAlert, Trash2, Pencil, Link2, CheckCircle2, Flag, Sparkles, CalendarClock, Radar,
} from "lucide-react";
import OnTheRadar from "@/components/OnTheRadar";
import ParkFields, { validateParked, ParkedBadge } from "@/components/ParkFields";

const SUBTABS = [
  ["overview", "Overview", Flag],
  ["risks", "Risks & Issues", ShieldAlert],
  ["actions", "Action items", ClipboardList],
  ["decisions", "Decisions", Scale],
  ["radar", "On the Radar", Radar],
];

export default function RaidView({ projectId, tasks = [], initialSub = "overview" }) {
  const [sub, setSub] = useState(initialSub);
  const [raid, setRaid] = useState(null);

  const load = () => api.get(`/projects/${projectId}/raid`).then(({ data }) => setRaid(data)).catch(() => {});
  useEffect(() => { load(); }, [projectId, sub]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-5" data-testid="raid-view">
      <div className="flex flex-wrap items-center gap-2">
        <SeeItInAction chapter={7} className="ml-auto order-last" />
        {SUBTABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setSub(k)}
            className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${sub === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary/50"}`}
            data-testid={`raid-subtab-${k}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
            {raid && k === "decisions" && raid.summary.open_decisions > 0 && <span className="text-[10px] font-bold bg-white/25 rounded-full px-1.5">{raid.summary.open_decisions}</span>}
            {raid && k === "radar" && (raid.summary.parked || 0) > 0 && <span className={`text-[10px] font-bold rounded-full px-1.5 ${raid.summary.parked_due ? "bg-red-500 text-white" : "bg-white/25"}`} data-testid="raid-radar-badge">{raid.summary.parked}</span>}
          </button>
        ))}
      </div>
      {sub === "overview" && <RaidOverview raid={raid} onGo={setSub} />}
      {sub === "risks" && <RiskRegister projectId={projectId} />}
      {sub === "actions" && <ActionItems projectId={projectId} tasks={tasks} />}
      {sub === "decisions" && <DecisionsManager projectId={projectId} tasks={tasks} raid={raid} onChanged={load} />}
      {sub === "radar" && <OnTheRadar projectId={projectId} onGo={(s) => { setSub(s); load(); }} />}
    </div>
  );
}

function RaidOverview({ raid, onGo }) {
  if (!raid) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const s = raid.summary;
  const stats = [
    { k: "risks", label: "Open risks", val: s.open_risks, color: "#DC2626", go: "risks" },
    { k: "issues", label: "Open issues", val: s.open_issues, color: "#EA580C", go: "risks" },
    { k: "actions", label: "Open actions", val: s.open_actions, sub: s.late_actions ? `${s.late_actions} late` : null, color: "#2563EB", go: "actions" },
    { k: "decisions", label: "Open decisions", val: s.open_decisions, sub: s.blocking_decisions ? `${s.blocking_decisions} blocking` : null, color: "#7C3AED", go: "decisions" },
  ];
  const blocking = raid.decisions.filter((d) => d.status === "open" && d.blocking);
  const topRisks = raid.risks.filter((r) => r.status !== "closed").sort((a, b) => (b.probability * b.impact) - (a.probability * a.impact)).slice(0, 5);
  return (
    <div className="space-y-5" data-testid="raid-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((st) => (
          <button key={st.k} onClick={() => onGo(st.go)} className="border border-border rounded-lg p-4 text-left hover:shadow-sm hover:border-foreground/30 transition-all" data-testid={`raid-stat-${st.k}`}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{st.label}</p>
            <p className="font-display text-3xl font-extrabold mt-1" style={{ color: st.color }}>{st.val}</p>
            {st.sub && <p className="text-xs font-semibold text-red-500 mt-0.5">{st.sub}</p>}
          </button>
        ))}
      </div>
      {blocking.length > 0 && (
        <div className="border border-violet-300 bg-violet-50 rounded-lg p-4" data-testid="raid-blocking-banner">
          <p className="font-bold text-sm text-violet-800 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {blocking.length} open decision(s) blocking progress</p>
          <ul className="mt-2 space-y-1">
            {blocking.map((d) => (
              <li key={d.id} className="text-sm text-violet-900">• <b>{d.title}</b>{d.due_date ? ` — needed by ${d.due_date}` : ""} · slows Auto Pilot forecasts on {(d.linked_task_ids || []).length} linked task(s)</li>
            ))}
          </ul>
        </div>
      )}
      {topRisks.length > 0 && (
        <div className="border border-border rounded-lg p-4">
          <p className="font-bold text-sm mb-2">Top risks by score</p>
          <ul className="space-y-1.5">
            {topRisks.map((r) => (
              <li key={r.id} className="text-sm flex items-center gap-2">
                <span className="h-2 w-2 rounded-full shrink-0" style={{ background: r.probability * r.impact >= 15 ? "#DC2626" : r.probability * r.impact >= 8 ? "#F59E0B" : "#10B981" }} />
                <span className="truncate flex-1">{r.title}</span>
                <span className="text-xs text-muted-foreground shrink-0">{r.owner || "unowned"} · score {r.probability * r.impact}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const EMPTY = { title: "", description: "", status: "open", owner: "", due_date: "", decided_on: "", blocking: false, decision: "", framework: "", framework_data: {}, maestro_recommendation: "", maestro_generated_at: null, linked_task_ids: [], linked_risk_ids: [], linked_action_ids: [], linked_stakeholder_ids: [], parked_reason: "", parked_revisit: "" };

function DecisionsManager({ projectId, tasks, raid, onChanged }) {
  const [frameworks, setFrameworks] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [modelView, setModelView] = useState(null);

  const runMaestro = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/decisions/analyze`, {
        title: form.title, description: form.description || "", framework: form.framework,
        due_date: form.due_date || null, blocking: !!form.blocking,
        linked_task_ids: form.linked_task_ids || [], linked_risk_ids: form.linked_risk_ids || [],
      });
      setForm((f) => ({ ...f, framework_data: { ...f.framework_data, ...data.framework_data },
        maestro_recommendation: data.recommendation, maestro_generated_at: data.generated_at }));
      toast.success("Maestro filled the analysis — review and edit freely");
    } catch (e) { toast.error(e.response?.data?.detail || "Analysis failed — try again"); }
    finally { setAnalyzing(false); }
  };
  const [stakeholders, setStakeholders] = useState([]);

  useEffect(() => {
    api.get("/decision-frameworks").then(({ data }) => setFrameworks(data)).catch(() => {});
    api.get(`/projects/${projectId}/stakeholders`).then(({ data }) => setStakeholders(Array.isArray(data) ? data : data?.stakeholders || [])).catch(() => {});
  }, [projectId]);

  const decisions = raid?.decisions || [];
  const risks = raid ? [...raid.risks, ...raid.issues] : [];
  const actions = raid?.actions || [];

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (d) => { setForm({ ...EMPTY, ...d, due_date: d.due_date || "", decided_on: d.decided_at ? d.decided_at.slice(0, 10) : "" }); setEditId(d.id); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Give the decision a title"); return; }
    if (!validateParked(form, toast.error)) return;
    setSaving(true);
    try {
      const body = { ...form, due_date: form.due_date || null, parked_revisit: form.parked_revisit || null };
      delete body.id; delete body.project_id; delete body.overdue; delete body.created_at; delete body.decided_at; delete body.project_name;
      if (editId) await api.put(`/decisions/${editId}`, body);
      else await api.post(`/projects/${projectId}/decisions`, body);
      toast.success(editId ? "Decision updated" : "Decision point added");
      setOpen(false);
      onChanged();
    } catch (e) { toast.error("Could not save decision"); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/decisions/${id}`); toast.success("Decision removed"); onChanged(); } catch (e) { toast.error("Delete failed"); }
  };

  const markDecided = async (d) => {
    try {
      const body = { ...d, status: "decided", due_date: d.due_date || null, decided_on: new Date().toISOString().slice(0, 10) };
      delete body.id; delete body.project_id; delete body.overdue; delete body.created_at; delete body.decided_at; delete body.project_name;
      await api.put(`/decisions/${d.id}`, body);
      toast.success("Marked decided");
      onChanged();
    } catch (e) { toast.error("Update failed"); }
  };

  const fwSections = form.framework ? (frameworks[form.framework]?.sections || []) : [];
  const toggleLink = (field, id) => setForm((f) => ({ ...f, [field]: f[field].includes(id) ? f[field].filter((x) => x !== id) : [...f[field], id] }));

  const dstats = [
    { k: "open", label: "Open", val: decisions.filter((d) => d.status === "open").length, color: "#2563EB", icon: Scale },
    { k: "blocking", label: "Blocking", val: decisions.filter((d) => d.status === "open" && d.blocking).length, color: "#7C3AED", icon: AlertTriangle },
    { k: "overdue", label: "Overdue", val: decisions.filter((d) => d.overdue).length, color: "#EF4444", icon: Flag },
    { k: "decided", label: "Decided", val: decisions.filter((d) => d.status === "decided").length, color: "#10B981", icon: CheckCircle2 },
    (() => {
      const closed = decisions.filter((d) => d.status === "decided" && d.decided_at && d.created_at);
      const avg = closed.length ? Math.round(closed.reduce((s, d) => s + Math.max(0, (Date.parse(d.decided_at) - Date.parse(d.created_at)) / 86400000), 0) / closed.length) : null;
      return { k: "avg-close", label: "Avg days to close", val: avg == null ? "—" : avg, color: "#0EA5E9", icon: CalendarClock };
    })(),
  ];

  return (
    <div className="space-y-4" data-testid="decisions-manager">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold">Decisions <span className="text-sm text-muted-foreground font-normal">({decisions.length})</span></h3>
        <Button size="sm" className="gap-1.5 shrink-0" onClick={openNew} data-testid="decision-add-button"><Plus className="h-4 w-4" /> New decision</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="decision-stats">
        {dstats.map((st) => (
          <div key={st.k} className="border border-border rounded-lg p-5" data-testid={`decision-kpi-${st.k}`}>
            <div className="flex items-center justify-between">
              <p className="overline text-muted-foreground">{st.label}</p>
              <st.icon className="h-4 w-4" style={{ color: st.color }} />
            </div>
            <p className="font-display text-3xl font-extrabold tracking-tight mt-2" style={{ color: st.color }}>{st.val}</p>
          </div>
        ))}
      </div>
      <p className="text-sm text-muted-foreground">Track the calls that need to be made — link them to the work they block, pick a decision model, and record the outcome. Click any line item to open it.</p>
      {!raid ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        : decisions.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-10 text-center text-sm text-muted-foreground" data-testid="decisions-empty">
          <Scale className="h-7 w-7 text-primary mx-auto mb-2" /> No decision points yet — add the first one.
        </div>
      ) : (
        <div className="space-y-2.5">
          {decisions.map((d) => {
            const links = (d.linked_task_ids || []).length + (d.linked_risk_ids || []).length + (d.linked_action_ids || []).length + (d.linked_stakeholder_ids || []).length;
            return (
              <div key={d.id} className={`border rounded-lg p-4 ${d.overdue ? "border-red-300 bg-red-50" : d.status === "decided" ? "border-emerald-200 bg-emerald-50/40" : d.blocking ? "border-violet-300 bg-violet-50/50" : "border-border"}`} data-testid={`decision-row-${d.id}`}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                      {d.title}
                      {d.status === "decided" ? <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Decided</span>
                        : d.status === "parked" ? <ParkedBadge item={d} testid={`decision-parked-${d.id}`} />
                        : d.overdue ? <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-red-500 text-white">Overdue</span>
                        : <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">Open</span>}
                      {d.blocking && d.status === "open" && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-violet-600 text-white" data-testid={`decision-blocking-${d.id}`}>Blocking</span>}
                      {d.framework && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); setModelView(d); }}
                          className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200 transition-colors inline-flex items-center gap-1"
                          title="View the completed decision model" data-testid={`decision-model-badge-${d.id}`}>
                          <Scale className="h-2.5 w-2.5" /> {frameworks[d.framework]?.label || d.framework}
                        </button>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {d.owner ? `Owner: ${d.owner}` : "No owner"}{d.due_date ? ` · needed by ${d.due_date}` : ""}{links ? ` · ${links} linked item(s)` : ""}
                      {d.status === "decided" && d.decided_at && (() => {
                        const days = d.created_at ? Math.max(0, Math.round((Date.parse(d.decided_at) - Date.parse(d.created_at)) / 86400000)) : null;
                        return <span className="text-emerald-700 font-semibold" data-testid={`decision-closed-${d.id}`}> · decided {d.decided_at.slice(0, 10)}{days != null ? ` · closed in ${days} day${days === 1 ? "" : "s"}` : ""}</span>;
                      })()}
                    </p>
                    {d.status === "decided" && d.decision && <p className="text-sm mt-1.5 text-emerald-800"><b>Outcome:</b> {d.decision}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {d.status === "open" && <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => markDecided(d)} data-testid={`decision-decide-${d.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Decide</Button>}
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(d)} data-testid={`decision-edit-${d.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => remove(d.id)} data-testid={`decision-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="decision-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit decision point" : "New decision point"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>What needs to be decided?</Label>
              <Input className="mt-1.5" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Build vs buy the reporting module" data-testid="decision-title-input" />
            </div>
            <div>
              <Label>Context</Label>
              <Textarea className="mt-1.5" rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="decision-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Owner / decider</Label>
                <Input className="mt-1.5" value={form.owner} onChange={(e) => setForm((f) => ({ ...f, owner: e.target.value }))} data-testid="decision-owner-input" />
              </div>
              <div>
                <Label>Needed by</Label>
                <Input className="mt-1.5" type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="decision-due-input" />
              </div>
            </div>
            {form.status === "decided" && (
              <div>
                <Label>Close date (when it was decided)</Label>
                <Input className="mt-1.5" type="date" value={form.decided_on} onChange={(e) => setForm((f) => ({ ...f, decided_on: e.target.value }))} data-testid="decision-closed-input" />
                <p className="text-[10px] text-muted-foreground mt-1">Used for decision history and time-to-close stats.</p>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="decision-blocking-check">
              <Checkbox checked={form.blocking} onCheckedChange={(v) => setForm((f) => ({ ...f, blocking: !!v }))} />
              <span><b>Blocking progress</b> — Auto Pilot will slow forecasts on linked tasks until this is decided</span>
            </label>

            <div>
              <Label>Decision model (optional)</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <Select value={form.framework || "none"} onValueChange={(v) => setForm((f) => ({ ...f, framework: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="decision-framework-select"><SelectValue placeholder="Pick a strategy…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No model</SelectItem>
                    {Object.entries(frameworks).map(([k, fw]) => <SelectItem key={k} value={k} data-testid={`framework-opt-${k}`}>{fw.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.framework && (
                  <Button type="button" size="sm" variant="outline" className="gap-1.5 shrink-0 border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={runMaestro} disabled={analyzing || !form.title.trim()}
                    title="Maestro fills each section using this project's tasks, risks, dates and blockers"
                    data-testid="decision-maestro-analyze">
                    {analyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    {analyzing ? "Analyzing…" : (form.maestro_recommendation ? "Re-run Maestro" : "Fill with Maestro")}
                  </Button>
                )}
              </div>
              {form.framework && !form.title.trim() && <p className="text-[10px] text-muted-foreground mt-1">Give the decision a title, then Maestro can analyze it.</p>}
              {fwSections.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 border border-border rounded-lg p-3 bg-secondary/20" data-testid="framework-template">
                  {fwSections.map((s) => (
                    <div key={s}>
                      <Label className="text-xs">{s}</Label>
                      <Textarea rows={3} className="mt-1 text-sm" value={form.framework_data[s] || ""}
                        onChange={(e) => setForm((f) => ({ ...f, framework_data: { ...f.framework_data, [s]: e.target.value } }))}
                        data-testid={`framework-field-${s.toLowerCase().replace(/[^a-z]/g, "")}`} />
                    </div>
                  ))}
                </div>
              )}
              {form.maestro_recommendation && (
                <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50/60 p-3" data-testid="decision-maestro-recommendation">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> Maestro recommendation
                    {form.maestro_generated_at && <span className="font-normal normal-case tracking-normal">· {String(form.maestro_generated_at).slice(0, 10)}</span>}
                  </p>
                  <p className="text-sm mt-1">{form.maestro_recommendation}</p>
                </div>
              )}
            </div>

            <div className="border border-border rounded-lg p-3 space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> Linked items (what this decision influences)</p>
              <LinkPicker label="Tasks" items={tasks.map((t) => ({ id: t.id, name: t.title }))} selected={form.linked_task_ids} onToggle={(id) => toggleLink("linked_task_ids", id)} testid="tasks" />
              <LinkPicker label="Risks & issues" items={risks.map((r) => ({ id: r.id, name: r.title }))} selected={form.linked_risk_ids} onToggle={(id) => toggleLink("linked_risk_ids", id)} testid="risks" />
              <LinkPicker label="Action items" items={actions.map((a) => ({ id: a.id, name: a.title }))} selected={form.linked_action_ids} onToggle={(id) => toggleLink("linked_action_ids", id)} testid="actions" />
              <LinkPicker label="Stakeholders" items={stakeholders.map((s) => ({ id: s.key || s.id, name: s.name || s.key }))} selected={form.linked_stakeholder_ids} onToggle={(id) => toggleLink("linked_stakeholder_ids", id)} testid="stakeholders" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="decision-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="parked">On Hold / Parked</SelectItem>
                    <SelectItem value="decided">Decided</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <ParkFields form={form} set={(k, v) => setForm((f) => ({ ...f, [k]: v }))} testPrefix="decision" />
              </div>
              {form.status === "decided" && (
                <div>
                  <Label>Decision made</Label>
                  <Input className="mt-1.5" value={form.decision} onChange={(e) => setForm((f) => ({ ...f, decision: e.target.value }))} placeholder="What was decided" data-testid="decision-outcome-input" />
                </div>
              )}
            </div>
            <Button className="w-full gap-2" onClick={save} disabled={saving} data-testid="decision-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {editId ? "Save changes" : "Add decision point"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!modelView} onOpenChange={(v) => !v && setModelView(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="decision-model-dialog">
          {modelView && (() => {
            const fw = frameworks[modelView.framework] || { label: modelView.framework, sections: Object.keys(modelView.framework_data || {}) };
            const NAMED = {
              strengths: ["#059669", "#ECFDF5"], weaknesses: ["#DC2626", "#FEF2F2"], opportunities: ["#2563EB", "#EFF6FF"], threats: ["#B45309", "#FFFBEB"],
              pros: ["#059669", "#ECFDF5"], cons: ["#DC2626", "#FEF2F2"],
            };
            const FALLBACK = [["#2563EB", "#EFF6FF"], ["#7C3AED", "#F5F3FF"], ["#0D9488", "#F0FDFA"], ["#B45309", "#FFFBEB"], ["#DB2777", "#FDF2F8"]];
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display flex items-center gap-2">
                    <Scale className="h-5 w-5 text-primary" /> {fw.label}
                  </DialogTitle>
                  <DialogDescription>{modelView.title}</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(fw.sections || []).map((s, i) => {
                    const [color, bg] = NAMED[s.toLowerCase()] || FALLBACK[i % FALLBACK.length];
                    const val = (modelView.framework_data || {})[s] || "";
                    return (
                      <div key={s} className="rounded-lg border p-3.5" style={{ borderColor: `${color}55`, background: bg }} data-testid={`model-section-${s.toLowerCase().replace(/[^a-z]/g, "")}`}>
                        <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color }}>{s}</p>
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">{val.trim() || "—"}</p>
                      </div>
                    );
                  })}
                </div>
                {modelView.maestro_recommendation && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3.5" data-testid="model-recommendation">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" /> Maestro recommendation
                      {modelView.maestro_generated_at && <span className="font-normal normal-case tracking-normal">· {String(modelView.maestro_generated_at).slice(0, 10)}</span>}
                    </p>
                    <p className="text-sm mt-1">{modelView.maestro_recommendation}</p>
                  </div>
                )}
                {modelView.status === "decided" && modelView.decision && (
                  <p className="text-sm text-emerald-800"><b>Outcome:</b> {modelView.decision}</p>
                )}
                <Button variant="outline" className="w-full gap-2" onClick={() => { const d = modelView; setModelView(null); openEdit(d); }} data-testid="model-edit-button">
                  <Pencil className="h-4 w-4" /> Edit this model
                </Button>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LinkPicker({ label, items, selected, onToggle, testid }) {
  const [expand, setExpand] = useState(false);
  if (!items.length) return null;
  const shown = expand ? items : items.slice(0, 6);
  return (
    <div data-testid={`link-picker-${testid}`}>
      <p className="text-xs font-semibold mb-1">{label} <span className="text-muted-foreground font-normal">({selected.length} linked)</span></p>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((it) => (
          <button key={it.id} type="button" onClick={() => onToggle(it.id)}
            className={`text-xs px-2 py-1 rounded-full border transition-colors max-w-[220px] truncate ${selected.includes(it.id) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary/60"}`}
            data-testid={`link-opt-${testid}-${it.id}`}>{it.name}</button>
        ))}
        {items.length > 6 && <button type="button" className="text-xs text-primary font-semibold" onClick={() => setExpand((v) => !v)}>{expand ? "less" : `+${items.length - 6} more`}</button>}
      </div>
    </div>
  );
}
